import LastPageIcon from '@mui/icons-material/LastPage';
// theme css file

import { Button, Grid, Stack, Tooltip, Typography } from '@mui/material';
import { formatISO, isAfter, isBefore, isSameDay, parseISO, subDays } from 'date-fns';
import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RangeFocus, DateRange as ReactDateRange } from 'react-date-range';

import 'react-date-range/dist/styles.css';
// main style file
import 'react-date-range/dist/theme/default.css';

import { useDeviceLatestData } from '@/api/devices';
import { DEFAULT_HISTORICAL_DATA_DATE_RANGE_DISTANCE_DAYS, MAX_HISTORICAL_DATA_DATE_RANGE_DAYS } from '@/config';
import { useDateRangeContext } from '@/context/DateRangeContext';
import { useSpotterContext } from '@/context/SpotterContext';
import { useNow } from '@/hooks/useNow';
import { DateRange } from '@/store';
import { clampRangeToMaxSpan, earliestStartForSpan, latestEndForSpan } from '@/utils';

// cf wavefleet oceans-api.js's `const DAYS_BACK = 14`
const OCEANS_API_MAX_DAYS_BACK = 14;

/** The picker starts every visit on the start date, so nothing an abandoned selection left behind is read. */
const PICK_START_DATE: RangeFocus = [0, 0];

type RangeSelectProps = {
    [key: string]: {
        startDate?: Date;
        endDate?: Date;
        key?: string;
    };
};

export const DateRangeSelection = () => {
    const { viaOceansApi } = useSpotterContext();
    const { device } = useDeviceLatestData();
    const { startDateStamp, endDateStamp, startTs, setDateRange } = useDateRangeContext();
    const lastDataTime = device?.timeLastUpdatedUTC ?? device?.waves?.timestamp ?? device?.track?.[0]?.timestamp;
    const now = useNow(3600);
    const oceansApiMinDate = viaOceansApi ? subDays(now, OCEANS_API_MAX_DAYS_BACK) : undefined;
    /**
     * selectedRange is the current selection in the picker which may or may not have been committed to the context yet.
     * Because we're only selecting dates, our handling of selectedRange is _not_ TZ-sensitive -- if you pick "Jan 3",
     * you mean Jan 3, even if you're display preference is UTC and right now Jan 3 here might be Jan 2 in UTC.
     */
    const [selectedRange, setSelectedRange] = useState<DateRange | null>();

    useEffect(() => {
        if (!startDateStamp || !endDateStamp) {
            return;
        }

        setSelectedRange({
            startDate: parseISO(startDateStamp),
            endDate: parseISO(endDateStamp),
        });
    }, [endDateStamp, startDateStamp]);

    const [isDateRangePickerOpen, setIsDateRangePickerOpen] = useState(false);

    /**
     * Which end of the range the next click lands on, as `[rangeIndex, 0 | 1]`, where 1 is the end date.
     *
     * Held here rather than left to react-date-range, because passing `onRangeFocusChange` replaces the
     * handler it updates its own copy through. The value it hands back is the one to render next.
     */
    const [focusedRange, setFocusedRange] = useState<RangeFocus>(PICK_START_DATE);

    /*
     * The calendar stays mounted while the picker is closed, so opening it is what starts a fresh
     * selection. Without this the first click of a visit lands on the end of a range the user abandoned.
     */
    useEffect(() => {
        if (isDateRangePickerOpen) {
            setFocusedRange(PICK_START_DATE);
        }
    }, [isDateRangePickerOpen]);

    /**
     * The date the span is measured from while the end of the range is being picked.
     *
     * Closing the days outside the widest span either side of it is what makes the limit something the
     * user runs into rather than something that rewrites the date they chose.
     */
    const spanAnchor = focusedRange[1] === 1 ? selectedRange?.startDate : undefined;

    /**
     * Whether `date` sits outside the widest span either side of the start it is measured from.
     *
     * react-date-range reads `minDate` and `maxDate` to clamp month navigation and to fill the year
     * dropdown as well as to close day cells, so the span limit reaches the calendar as a per-day
     * predicate: the calendar stays free to walk to any month while the days a selection cannot reach
     * from there are closed. Both edges are closed, because react-date-range swaps the two dates when the
     * second click lands before the first, so the floor is what holds the span for a range picked
     * backwards.
     */
    const isOutsideMaxSpan = useCallback(
        (date: Date) => {
            if (!spanAnchor) {
                return false;
            }
            return (
                isBefore(date, earliestStartForSpan(spanAnchor, MAX_HISTORICAL_DATA_DATE_RANGE_DAYS)) ||
                isAfter(date, latestEndForSpan(spanAnchor, MAX_HISTORICAL_DATA_DATE_RANGE_DAYS))
            );
        },
        [spanAnchor]
    );

    /**
     * Held still across a render that changed nothing about the selection.
     *
     * react-date-range compares this prop by reference, and walks its view back to the range it holds
     * whenever it differs. `DateRangeContext` mints a fresh value every time a live `endTs` advances, which
     * is once a second while a device reports that often, so a fresh array here puts the calendar back on
     * the current month faster than it can be navigated away from.
     */
    const ranges = useMemo(() => [{ ...selectedRange, key: 'selection' }], [selectedRange]);

    const dataPickerContainerRef = useRef() as RefObject<HTMLDivElement>;

    const getStartEndDatesFromLastDataTime = (lastDataTime: string | number): { startDate: Date; endDate: Date } => {
        const endDate = new Date(lastDataTime);
        const startDate = subDays(endDate, DEFAULT_HISTORICAL_DATA_DATE_RANGE_DISTANCE_DAYS);
        return { startDate, endDate };
    };

    const closePicker = useCallback(
        (newRange?: DateRange) => {
            // if a new range was passed in, use that, otherwise use whatever the latest selection was
            // (can't setSelectedRange and then immediately closePicker because it won't pick up the new selectedRange in time )
            const range = newRange ?? selectedRange;
            if (range) {
                setDateRange(
                    formatISO(range.startDate, { representation: 'date' }),
                    formatISO(range.endDate, { representation: 'date' })
                );
            }
            setIsDateRangePickerOpen(false);
        },
        [selectedRange, setDateRange, setIsDateRangePickerOpen]
    );

    const onClickRecentData = () => {
        if (!lastDataTime) return;

        closePicker(getStartEndDatesFromLastDataTime(lastDataTime));
    };

    const handleSelect = (ranges: RangeSelectProps) => {
        const { startDate, endDate } = ranges.selection;
        if (!startDate || !endDate) {
            return;
        }

        const isStartDateChanged = !selectedRange?.startDate || !isSameDay(selectedRange.startDate, startDate);

        /*
         * The closed days above stop the calendar handing back an over-long span, apart from one hole:
         * `DayCell` reads its `disabled` prop on pointer events and not on Enter or Space. This is what
         * holds the span whatever route the selection arrived by, and it keeps the picker in agreement
         * with DateRangeContext, which clamps whatever it is given. The edge that just moved is the one
         * the user means, so an over-long span comes off the other one.
         */
        const selection = clampRangeToMaxSpan(
            { startDate, endDate },
            MAX_HISTORICAL_DATA_DATE_RANGE_DAYS,
            isStartDateChanged ? 'start' : 'end'
        );

        setSelectedRange(selection);

        /*
         * `focusedRange[1] === 1` means the user is selecting the end date, so this change completes
         * the range. `onChange` runs before `onRangeFocusChange`, which means `focusedRange` still
         * describes the selection phase that triggered this change.
         *
         * We cannot detect completion by checking which date changed because react-date-range always
         * sorts the dates. If the user selects an earlier date second, the start date changes instead.
         */
        if (focusedRange[1] === 1) {
            closePicker(selection);
        }
    };

    const handleClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        e.stopPropagation();
        const isInputButtonsClick = (e.target as HTMLElement).parentElement?.classList.contains('rdrDateInput');
        if (!isInputButtonsClick) {
            return;
        }
        const isActiveButton = (e.target as HTMLElement).parentElement?.classList.contains('rdrDateDisplayItemActive');
        if (isActiveButton && isDateRangePickerOpen) {
            // disregard click on an active button when the calendar is open
            return;
        }
        if (isDateRangePickerOpen) {
            closePicker();
            return;
        }
        setIsDateRangePickerOpen(true);
    };

    useEffect(() => {
        const handleClickOutside = (e: Event) => {
            if (dataPickerContainerRef.current?.contains(e.target as Node)) {
                return;
            }
            closePicker();
        };
        window.addEventListener('click', handleClickOutside);
        return () => {
            window.removeEventListener('click', handleClickOutside);
        };
    }, [closePicker]);

    const timeRangePastMostRecentData = useMemo(() => {
        if (!lastDataTime || !startTs) return false;

        return isAfter(startTs, new Date(lastDataTime));
    }, [lastDataTime, startTs]);

    return (
        <Grid
            container
            ref={dataPickerContainerRef}
            onClick={handleClick}
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={2}
            sx={(theme) => ({
                flexDirection: 'row',
                flexWrap: 'wrap',
                '.rdrDateRangeWrapper.rdrCalendarWrapper': {
                    position: 'relative',
                    flex: 10,
                    width: '360px',
                },
                '.rdrDateDisplayWrapper': {
                    maxWidth: '100%',
                },
                '.rdrMonthAndYearWrapper': {
                    visibility: isDateRangePickerOpen ? 'visible' : 'hidden',
                    zIndex: theme.zIndex.appBar,
                    position: 'absolute',
                    top: 54,
                    backgroundColor: 'white',
                    border: '1px solid #eff2f7',
                    borderBottom: 'unset',
                    width: '100%',
                },
                '.rdrMonths.rdrMonthsHorizontal': {
                    visibility: isDateRangePickerOpen ? 'visible' : 'hidden',
                    zIndex: theme.zIndex.appBar,
                    justifyContent: 'space-around',
                    position: 'absolute',
                    top: 114,
                    backgroundColor: 'white',
                    width: '100%',
                    minWidth: '500px',
                    border: '1px solid #eff2f7',
                    borderTop: 'unset',
                },
                '.rdrMonths .rdrWeekDay': {
                    px: '2px',
                },
                '.rdrDateDisplayItem': {
                    borderRadius: 0,
                    boxShadow: 'none',
                },
            })}
        >
            <Typography variant="h4" component="span">
                Date range
            </Typography>
            <Stack direction="row" sx={{ backgroundColor: /* match .rdsDisplayWrapper */ 'rgb(239, 242, 247)' }}>
                <Typography component="div">
                    <ReactDateRange
                        ranges={ranges}
                        onChange={handleSelect}
                        focusedRange={focusedRange}
                        onRangeFocusChange={setFocusedRange}
                        moveRangeOnFirstSelection={false}
                        months={2}
                        minDate={oceansApiMinDate}
                        disabledDay={isOutsideMaxSpan}
                        direction="horizontal"
                    />
                </Typography>
                {lastDataTime && (
                    <Tooltip title="Jump to most recent data">
                        <Button
                            onClick={onClickRecentData}
                            variant={timeRangePastMostRecentData ? 'contained' : 'outlined'}
                            sx={{ p: 0.5, minWidth: 'unset', marginY: 'auto', marginRight: 1.5 }}
                        >
                            <LastPageIcon />
                        </Button>
                    </Tooltip>
                )}
            </Stack>
        </Grid>
    );
};
